import React, { useState, useEffect, useRef } from 'react';
import { Box, Button } from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { debounce } from 'lodash-es';
import { AppliedFiltersBar } from './AppliedFiltersBar';
import { OfferListFilters } from '../../types';
import { IDropdownOptions } from '@shared/interface';
import { SearchField } from '@shared/components/SearchField/SearchField';
import { DateRangeField } from '../../shared/components/DateRangeField';
import { FilterAlt } from '@mui/icons-material';

interface TableSearchBarProps {
  filters: OfferListFilters;
  onChange: (values: Partial<OfferListFilters>) => void;
  onFilterClick: () => void;
  filterCount: number;
  loading?: boolean;
  categoryOptions: IDropdownOptions[];
  currencyOptions: IDropdownOptions[];
  scopeOptions: IDropdownOptions[];
  countryOptions: IDropdownOptions[];
  onOpenColumnsMenu?: (anchorEl: HTMLElement) => void;
  columnsCount?: number | null;
}

export const TableSearchBar: React.FC<TableSearchBarProps> = ({
  filters,
  onChange,
  onFilterClick,
  filterCount,
  loading,
  categoryOptions,
  currencyOptions,
  scopeOptions,
  countryOptions,
  onOpenColumnsMenu,
  columnsCount,
}) => {
  const [input, setInput] = useState(filters.search || '');
  const debouncedOnChange = useRef(debounce((v: string) => onChange({ search: v }), 500));
  // no local state; DataTable owns the menu and visibility

  useEffect(() => {
    debouncedOnChange.current(input);
  }, [input]);

  useEffect(() => {
    setInput(filters.search || '');
  }, [filters.search]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        width: '100%',
      }}
    >
      <AppliedFiltersBar
        filters={filters}
        categoryOptions={categoryOptions}
        currencyOptions={currencyOptions}
        scopeOptions={scopeOptions}
        countryOptions={countryOptions}
        onRemove={(field) => onChange({ [field]: undefined })}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <SearchField
          value={input}
          onChange={setInput}
          placeholder="Search by name or id..."
          disabled={loading}
          size='medium'
        />
        <Box sx={{ minWidth: 230, height: 40 }}>
          <DateRangeField
            label="Date range"
            startDate={filters.dateFrom}
            endDate={filters.dateTo}
            onChange={(start, end) =>
              onChange({
                dateFrom: start || undefined,
                dateTo: end || undefined,
              })
            }
            fullWidth
          />
        </Box>
        <Button
          variant="outlined"
          startIcon={<FilterAlt />}
          onClick={onFilterClick}
          sx={{ minWidth: 120, position: 'relative', height: 40 }}
        >
          Filters {filterCount ? ` (${filterCount})` : ''}
        </Button>
        {onOpenColumnsMenu && (
          <Button
            variant="outlined"
            startIcon={<ViewColumnIcon />}
            onClick={(e) => onOpenColumnsMenu(e.currentTarget)}
            sx={{ minWidth: 140, height: 40 }}
          >
            {'Columns' + (typeof columnsCount === 'number' ? ' (' + columnsCount + ')' : '')}
          </Button>
        )}
      </Box>
    </Box>
  );
};
